import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import { toast } from 'sonner';
import { errorMessage } from '@/shared/api/errors';
import {
  DEFAULT_SLIP_TEMPLATES,
  loadSlipTemplates,
  saveSlipTemplate,
  type SlipKind,
  type SlipTemplate,
  type SlipTemplates,
} from '@/app/slipTemplates';

const QUERY_KEY = ['system-settings', 'slip-templates'] as const;

export function SlipTemplateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<SlipKind>('stock-in');
  const [drafts, setDrafts] = useState<SlipTemplates>(DEFAULT_SLIP_TEMPLATES);
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: ({ signal }) => loadSlipTemplates(signal),
    enabled: open,
  });

  useEffect(() => {
    if (open && query.data) setDrafts(query.data);
  }, [open, query.data]);

  const key = kind === 'stock-in' ? 'stockIn' : 'stockOut';
  const draft = drafts[key];
  const update = <K extends keyof SlipTemplate>(field: K, value: SlipTemplate[K]) =>
    setDrafts((current) => ({ ...current, [key]: { ...current[key], [field]: value } }));

  const mutation = useMutation({
    mutationFn: () => saveSlipTemplate(kind, draft),
    onSuccess: (saved) => {
      const next = { ...drafts, [key]: saved };
      setDrafts(next);
      queryClient.setQueryData(QUERY_KEY, next);
      toast.success(`${kind === 'stock-in' ? 'Stock In' : 'Stock Out'} slip template saved.`);
    },
    onError: (saveError) => toast.error(errorMessage(saveError)),
  });

  return (
    <Dialog open={open} onClose={mutation.isPending ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>Slip template editor</DialogTitle>
      <DialogContent dividers>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Configure the A4 portrait PDF used for single and bulk-generated slips. Dates always print as DD/MM/YYYY.
        </Typography>
        <Tabs value={kind} onChange={(_, value: SlipKind) => setKind(value)} aria-label="Slip type">
          <Tab value="stock-in" label="Stock In" />
          <Tab value="stock-out" label="Stock Out" />
        </Tabs>

        {query.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(300px, 0.85fr) minmax(380px, 1.15fr)' }, gap: 3, mt: 2 }}>
            <Stack spacing={2}>
              <TextField label="Document title" value={draft.title} onChange={(event) => update('title', event.target.value)} inputProps={{ maxLength: 80 }} />
              <TextField label="Brand text" value={draft.brandText} onChange={(event) => update('brandText', event.target.value)} inputProps={{ maxLength: 80 }} disabled={!draft.showBrand} />
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5 }}>
                <TextField label="Margin (mm)" type="number" value={draft.marginMm} onChange={(event) => update('marginMm', Number(event.target.value))} inputProps={{ min: 6, max: 24, step: 1 }} />
                <TextField label="Heading (pt)" type="number" value={draft.headingSize} onChange={(event) => update('headingSize', Number(event.target.value))} inputProps={{ min: 16, max: 32, step: 1 }} />
                <TextField label="Body (pt)" type="number" value={draft.bodySize} onChange={(event) => update('bodySize', Number(event.target.value))} inputProps={{ min: 7, max: 12, step: 0.5 }} />
              </Box>
              <TextField label="Picker label" value={draft.pickerLabel} onChange={(event) => update('pickerLabel', event.target.value)} inputProps={{ maxLength: 40 }} />
              <TextField label="Signature label" value={draft.signatureLabel} onChange={(event) => update('signatureLabel', event.target.value)} inputProps={{ maxLength: 40 }} />
              <Stack>
                <FormControlLabel control={<Switch checked={draft.showBrand} onChange={(event) => update('showBrand', event.target.checked)} />} label="Show brand text" />
                <FormControlLabel control={<Switch checked={draft.showAvailableQuantity} onChange={(event) => update('showAvailableQuantity', event.target.checked)} />} label="Show available quantity column" />
                <FormControlLabel control={<Switch checked={draft.shadeTableHeader} onChange={(event) => update('shadeTableHeader', event.target.checked)} />} label="Shade table header" />
              </Stack>
              <Button startIcon={<RestartAltIcon />} onClick={() => updateAll(setDrafts, key, DEFAULT_SLIP_TEMPLATES[key])}>
                Reset this draft
              </Button>
            </Stack>

            <SlipPreview template={draft} kind={kind} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>Close</Button>
        <Button variant="contained" startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={() => mutation.mutate()} disabled={query.isLoading || mutation.isPending}>
          Save template
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function updateAll(setDrafts: React.Dispatch<React.SetStateAction<SlipTemplates>>, key: 'stockIn' | 'stockOut', template: SlipTemplate) {
  setDrafts((current) => ({ ...current, [key]: { ...template } }));
}

function SlipPreview({ template, kind }: { template: SlipTemplate; kind: SlipKind }) {
  const headings = ['No.', 'Item Code', 'Item Name', 'Client Code', 'Product Code', 'Stock Group', 'Color', 'Size', 'Category', 'Order Qty', ...(template.showAvailableQuantity ? ['Avl Qty'] : []), 'Location'];
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">Live A4 landscape preview</Typography>
      <Paper variant="outlined" sx={{ mx: 'auto', mt: 1, width: '100%', maxWidth: 520, aspectRatio: '297 / 210', p: `${Math.max(12, template.marginMm * 1.15)}px`, bgcolor: '#fff', color: '#111', overflow: 'hidden', boxShadow: 2 }}>
        {template.showBrand && <Typography sx={{ textAlign: 'right', fontSize: 13, fontWeight: 800 }}>{template.brandText}</Typography>}
        <Typography sx={{ fontSize: Math.max(15, template.headingSize * 0.72), mt: template.showBrand ? 1.5 : 0, mb: 1.5 }}>{template.title}</Typography>
        <Typography sx={{ float: 'right', fontSize: 8 }}>14/08/2026</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '95px 1fr', fontSize: 8, mb: 1.5 }}>
          {(kind === 'stock-in'
            ? [['PO No.:', 'PO-2026-0042'], ['Slip No.:', 'SI202608140001'], ['From:', 'Supplier / origin'], ['Delivery To:', 'MH GLOBAL SDN. BHD. WH 03'], ['JS No.:', 'JS-2026-0088']]
            : [['Slip No.:', 'SO202608140001'], ['Delivery Order Number:', 'DO-2026-0159'], ['Sales Order Number:', 'SO-2026-0159'], ['From:', 'MH GLOBAL SDN. BHD. WH 03'], ['Delivery To:', 'Customer delivery address']]
          ).map(([label, value]) => <Box key={label} sx={{ display: 'contents' }}><Box>{label}</Box><Box>{value}</Box></Box>)}
        </Box>
        <Box component="table" sx={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse', fontSize: 5.5, '& th, & td': { p: 0.4, border: '1px solid #222', textAlign: 'left', verticalAlign: 'top', overflowWrap: 'anywhere' }, '& th': { bgcolor: template.shadeTableHeader ? '#eee' : '#fff', fontWeight: 'normal' } }}>
          <thead>
            <tr>{headings.map((heading) => <th key={heading}>{heading}</th>)}</tr>
          </thead>
          <tbody>
            {[1, 2].map((row) => (
              <tr key={row}>
                {headings.map((heading) => <td key={heading}>{heading === 'No.' ? row : heading === 'Item Name' ? 'Sample product description' : heading === 'Item Code' ? 'ITEM-001' : heading === 'Order Qty' || heading === 'Avl Qty' ? '10' : heading === 'Location' ? 'WH03-A-01' : '-'}</td>)}
              </tr>
            ))}
          </tbody>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 2, mt: 3, fontSize: 8 }}>
          <Box>{template.pickerLabel}:</Box><Box sx={{ borderBottom: '1px solid #111' }} />
          <Box>{template.signatureLabel}:</Box><Box sx={{ borderBottom: '1px solid #111' }} />
        </Box>
      </Paper>
    </Box>
  );
}
